// Quote-level "Schedule" control (formerly "When do you need it?") — the
// "speed presets" design.
//
// The customer picks a lane, not a raw date: Standard (our ~1-week turnaround,
// no fee) or "I need it sooner" (a rush, which Nexus prices as a +10% line).
// The rush lane is an EXPLICIT choice, persisted on Quote.expedite_requested
// and passed in as `expedited` — the selected lane reflects that stored flag,
// NOT a date comparison. (Deriving it from Need-By vs "today" drifted a
// Standard quote into "expedited" as the clock advanced, since the Need-By is
// frozen at creation + the standard window.) The two cards set both the flag
// and a sensible date; a later-than-standard date is still no-fee and reachable
// from the Standard lane via the "specific date" disclosure.
//
// IMPORTANT: capturing "is this a rush?" is an explicit customer selection. The
// +10% itself is computed and owned by Nexus and shows up as its own line when
// the customer sees their price — the portal never computes the amount
// (CLAUDE.md §"Pricing logic lives in the ERP").

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { STANDARD_TURNAROUND_DAYS, addCalendarDays, defaultNeedByDate } from 'shared';

import { formatDate } from '../i18n/formatters.js';
import { FieldReadBack } from './FieldReadBack.js';

// Friendly label for a `YYYY-MM-DD` date, e.g. "Tue, Jul 2". Parsed as local
// midnight (not `new Date(iso)`, which is UTC and can shift a day west of GMT).
// Routed through the shared `formatDate` so the weekday/month names follow the
// active locale (Intl), never a hand-built / browser-default string.
function formatNeedByLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map((p) => Number.parseInt(p, 10));
  if (!y || !m || !d) return iso;
  return formatDate(new Date(y, m - 1, d), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// Customer-voice phrase for a turnaround window, e.g. "about a week" (7 days) or
// "about 3 weeks" (21 days, hard-bound). Keeps the copy honest when a longer
// window (a hard-bound project) stretches the order out.
// Module-level helper (not a component) — uses the i18next singleton directly
// with a fully-qualified `quote:` key, per the portal's i18n convention.
function turnaroundBlurb(days: number): string {
  if (days <= STANDARD_TURNAROUND_DAYS) return i18n.t('quote:needBy.turnaround.standard');
  const weeks = Math.round(days / 7);
  return i18n.t('quote:needBy.turnaround.weeks', { count: weeks });
}

type Props = {
  // The customer's local "today" as `YYYY-MM-DD` (the host computes it so the
  // default Need-By + the rush check line up with the customer's calendar).
  today: string;
  // The order's standard production window in calendar days — the longest across
  // its projects (a hard-bound book stretches it out). Drives the default
  // Need-By AND the rush threshold, so both reflect the actual product mix.
  // Defaults to the shop-wide standard when the host doesn't compute one.
  turnaroundDays?: number;
  // The current Need-By date (`Quote.requested_finish_date`), '' when unset.
  value: string;
  onChange: (next: string) => void;
  // The customer's explicit rush choice (`Quote.expedite_requested`). Drives
  // which lane reads as selected — the authoritative intent, not date-derived.
  expedited: boolean;
  onExpeditedChange: (next: boolean) => void;
  // How many items the order holds. Two or more and the scope line says so with
  // the count and points at the one place a differently-timed item can be
  // raised (that item's Notes) — the date itself stays one per order.
  itemCount?: number;
  // 'embedded' is the mount INSIDE the first item's configurator: same control,
  // stripped of the section chrome its item-owned neighbours carry, so it reads
  // as a quote-level aside rather than one more step in that item. 'standalone'
  // is the card above the items, which keeps the full section weight.
  variant?: 'standalone' | 'embedded';
  // Past editing (submitted / paid / cancelled / rep-owned). The two lane cards
  // are `<button role="radio">`, so there is no disabled rendering that keeps the
  // chosen lane readable — a natively disabled radio leaves the tab order and
  // takes its aria-checked with it. So this renders the ANSWER instead: the
  // chosen lane's card with no button around it, the lane that was not chosen
  // dropped, and the date read back beside it (ADR-018 phase 13/14).
  disabled?: boolean;
};

export function NeedByPicker({
  today,
  turnaroundDays = STANDARD_TURNAROUND_DAYS,
  value,
  onChange,
  expedited,
  onExpeditedChange,
  itemCount = 1,
  variant = 'standalone',
  disabled = false,
}: Props): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);

  // Stable ids so the radiogroup borrows the heading as its accessible name
  // (no duplicate announcement), the scope line is read out with it rather than
  // left to the eye alone, and the disclosure points at the panel it opens.
  const headingId = useId();
  const scopeId = useId();
  const laterPanelId = useId();
  const sectionClass = `config-section${variant === 'embedded' ? ' need-by--embedded' : ''}`;
  // A one-item order has nothing to share the date with, so it keeps the plain
  // sentence; the count only earns its words once items can differ. A locked
  // order takes the plain sentence too — the multi-item line ends in a "tell us
  // in that item's Notes", and Notes is as read-only as this card by then.
  // `items`, not `count`: an i18next `count` triggers plural-suffix lookup, and
  // this string has no _one/_other pair to resolve to.
  const scopeHint =
    itemCount > 1 && !disabled
      ? t('needBy.scopeHintMulti', { items: itemCount })
      : t('needBy.scopeHint');

  const defaultNeedBy = defaultNeedByDate(today, turnaroundDays);
  const effective = value || defaultNeedBy;
  const windowBlurb = turnaroundBlurb(turnaroundDays);
  // A date LATER than standard is still no-fee — surfaced under the Standard
  // card via the "specific date" disclosure rather than as its own lane.
  const isLater = !expedited && effective > defaultNeedBy;

  // Disclosure for the Standard-lane date picker. Opens automatically when the
  // saved date is already later than standard so it isn't hidden on load.
  const [showDate, setShowDate] = useState(isLater);

  // Latest date the rush lane offers — the day before standard, so any pick
  // there stays a rush (keeps the "I need it sooner" card consistently
  // selected). Date math via the shared helper (UTC-safe, no TZ drift).
  const latestRush = addCalendarDays(defaultNeedBy, -1);
  // Starting point when the customer first switches into the rush lane: a few
  // days inside the standard window so it reads as a real rush they can then
  // fine-tune. Falls back to `latestRush` if the turnaround window is ever
  // shortened to <4 days (so the seed never lands on or after `today`).
  const earlyRush = addCalendarDays(defaultNeedBy, -3);
  const rushStart = earlyRush > today ? earlyRush : latestRush;

  function chooseStandard(): void {
    onExpeditedChange(false);
    onChange(defaultNeedBy);
    setShowDate(false);
  }

  function chooseSooner(): void {
    onExpeditedChange(true);
    // Keep the customer's date if they're already in the rush lane; otherwise
    // seed a sensible rushed date they can adjust. Either way the rush callout
    // + constrained picker appear immediately.
    if (!expedited) onChange(rushStart);
    setShowDate(false);
  }

  if (disabled) {
    return (
      <div className={sectionClass}>
        <div className="config-section__title-row">
          <h3 className="config-section__title" id={headingId}>
            {t('needBy.title')}
          </h3>
        </div>
        <p className="help need-by-eyebrow">{scopeHint}</p>
        {/* The heading is the question, so the card needs no label of its own.
            It sheds the "ready by" date line every editable card carries: that
            line falls back to `defaultNeedBy`, which is computed from `today` and
            would walk forward a day at a time on an order that is already placed
            — presented as the customer's answer. The date below is the stored one
            or nothing, for the same reason the lane reads off the stored flag. */}
        <div className="need-by-speed__option need-by-speed__option--selected need-by-speed__option--locked">
          <span className="need-by-speed__name">
            {expedited ? t('needBy.sooner.name') : t('needBy.standard.name')}
          </span>
          <span className="need-by-speed__detail">
            {expedited
              ? t('needBy.sooner.detail')
              : t('needBy.standard.detail', { window: windowBlurb })}
          </span>
          <span
            className={`need-by-speed__tag need-by-speed__tag--${expedited ? 'warn' : 'ok'}`}
          >
            {expedited ? t('needBy.sooner.rushFeeTag') : t('needBy.standard.noFeeTag')}
          </span>
        </div>
        <FieldReadBack
          id="finish_date"
          label={t('needBy.inHandsByLabel')}
          value={value ? formatNeedByLabel(value) : undefined}
        />
      </div>
    );
  }

  return (
    <div className={sectionClass}>
      <div className="config-section__title-row">
        {/* h3 in both mounts: a line's own section titles are h3 too (SchemaForm
            forces that level in line scope), and this card is their peer, not a
            child of the Proofs section it lands after — stepping the TAG down
            would say the opposite to a screen reader. The embedded mount steps
            the SIZE down instead, in CSS. */}
        <h3 className="config-section__title" id={headingId}>
          {t('needBy.title')}
        </h3>
      </div>
      <p className="help need-by-eyebrow" id={scopeId}>
        {scopeHint}
      </p>

      <div
        className="need-by-speed"
        role="radiogroup"
        aria-labelledby={headingId}
        aria-describedby={scopeId}
      >
        <button
          type="button"
          role="radio"
          aria-checked={!expedited}
          className={`need-by-speed__option${!expedited ? ' need-by-speed__option--selected' : ''}`}
          onClick={chooseStandard}
        >
          <span className="need-by-speed__name">{t('needBy.standard.name')}</span>
          <span className="need-by-speed__detail">
            {t('needBy.standard.detail', { window: windowBlurb })}
          </span>
          <span className="need-by-speed__date">
            {t('needBy.standard.readyBy', {
              date: formatNeedByLabel(isLater ? effective : defaultNeedBy),
            })}
          </span>
          <span className="need-by-speed__tag need-by-speed__tag--ok">
            {t('needBy.standard.noFeeTag')}
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={expedited}
          className={`need-by-speed__option${expedited ? ' need-by-speed__option--selected' : ''}`}
          onClick={chooseSooner}
        >
          <span className="need-by-speed__name">{t('needBy.sooner.name')}</span>
          <span className="need-by-speed__detail">{t('needBy.sooner.detail')}</span>
          <span className="need-by-speed__tag need-by-speed__tag--warn">
            {t('needBy.sooner.rushFeeTag')}
          </span>
        </button>
      </div>

      {expedited ? (
        // Rush lane: a date picker bounded to the rush window (so it can't slip
        // back to a non-rush date without flipping lanes) + the fee callout.
        <div className="need-by-detail">
          <label htmlFor="finish_date" className="need-by-detail__label">
            {t('needBy.inHandsByLabel')}
          </label>
          <input
            id="finish_date"
            type="date"
            value={value || rushStart}
            min={today}
            max={latestRush}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="help">
            {t('needBy.rushLane.pickBeforeHint', { date: formatNeedByLabel(defaultNeedBy) })}
          </span>

          {/* Customer-voice — name the rush + the 10% fee plainly; the amount
              itself is computed by Nexus and appears as its own line on the
              price. Warn palette + bold heading carry the signal (not color
              alone, per the UX checklist). */}
          <div className="banner banner--warn expedite-callout" role="status">
            <svg
              className="expedite-callout__icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <div className="expedite-callout__body">
              <strong>{t('needBy.rushLane.bannerTitle')}</strong>
              <span>
                {t('needBy.rushLane.bannerBody', {
                  window: windowBlurb,
                  date: formatNeedByLabel(defaultNeedBy),
                })}
              </span>
            </div>
          </div>
        </div>
      ) : (
        // Standard lane: the ~1-week default is set. A quiet disclosure reveals a
        // date picker for customers who want a specific (later) day — no fee.
        <div className="need-by-detail">
          <button
            type="button"
            className="need-by-disclosure"
            aria-expanded={showDate}
            aria-controls={laterPanelId}
            onClick={() => setShowDate((v) => !v)}
          >
            <span aria-hidden="true">{showDate ? '▾' : '▸'}</span>{' '}
            {showDate ? t('needBy.standardLane.collapseCta') : t('needBy.standardLane.expandCta')}
          </button>
          {showDate && (
            <div id={laterPanelId} className="need-by-later">
              <label htmlFor="finish_date" className="need-by-detail__label">
                {t('needBy.inHandsByLabel')}
              </label>
              <input
                id="finish_date"
                type="date"
                value={effective}
                min={defaultNeedBy}
                onChange={(e) => onChange(e.target.value)}
              />
              <span className="help">
                {t('needBy.standardLane.laterDateHint', {
                  window: windowBlurb,
                  date: formatNeedByLabel(defaultNeedBy),
                  sooner: t('needBy.sooner.name'),
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
